import { useEffect, useRef, useState } from "react";

/**
 * Keeps a conditionally-rendered overlay (drawer / modal / backdrop)
 * mounted through its EXIT animation so it can play `.bk-anim-*-out`
 * (DESIGN.md §6 — "Overlays animate in AND out. No instant unmount.")
 * instead of vanishing the instant `open` flips false.
 *
 * Usage:
 *   const { rendered, closing } = useExitTransition(open);
 *   if (!rendered) return null;
 *   <aside className={closing ? "bk-anim-drawer-out" : "bk-anim-drawer"} />
 *
 * Contract:
 *   • `rendered` — whether the overlay should be in the DOM at all. Stays
 *     true for `durationMs` after `open` flips false so the exit animation
 *     can run, then flips false (unmount).
 *   • `closing` — whether the exit animation should be applied right now
 *     (open was true, is now false, still within the exit window).
 *   • When motion is reduced (the same `data-motion="reduce"` /
 *     `prefers-reduced-motion` contract honored in globals.css) the exit is
 *     INSTANT — no lingering frame — matching the reduced-motion intent.
 */
export interface ExitTransition {
  rendered: boolean;
  closing: boolean;
}

function motionIsReduced(): boolean {
  if (typeof document === "undefined") return false;
  const attr = document.documentElement.getAttribute("data-motion");
  if (attr === "reduce") return true;
  // Only consult the OS preference when the app hasn't pinned a value
  // (mirrors the `:root:not([data-motion])` guard in globals.css).
  if (attr === null && typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  }
  return false;
}

export function useExitTransition(open: boolean, durationMs = 200): ExitTransition {
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setRendered(true);
      setClosing(false);
      return;
    }

    // open === false
    if (!rendered) return; // already unmounted — nothing to animate out

    if (motionIsReduced()) {
      setRendered(false);
      setClosing(false);
      return;
    }

    setClosing(true);
    timerRef.current = setTimeout(() => {
      setRendered(false);
      setClosing(false);
      timerRef.current = null;
    }, durationMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, durationMs, rendered]);

  return { rendered, closing };
}
